import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BookingPage from './pages/BookingPage';
import ConnectPage from './pages/ConnectPage';
import { getSafeNext } from './utils/safeNext';
import DashboardLayout from './components/dashboard/DashboardLayout';
import OverviewPage from './pages/dashboard/OverviewPage';
import InboxPage from './pages/dashboard/InboxPage';
import CustomersPage from './pages/dashboard/CustomersPage';
import ChannelsPage from './pages/dashboard/ChannelsPage';
import IntegrationsPage from './pages/dashboard/IntegrationsPage';
import AISettingsPage from './pages/dashboard/AISettingsPage';
import BookingsPage from './pages/dashboard/BookingsPage';
import BillingPage from './pages/dashboard/BillingPage';
import AgentsPage from './pages/dashboard/AgentsPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import MyAccountPage from './pages/dashboard/MyAccountPage';
import CRMPage from './pages/dashboard/CRMPage';
import ExtractionTemplatesPage from './pages/dashboard/ExtractionTemplatesPage';

// Super Admin Pages
import AdminLayout from './components/dashboard/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMerchants from './pages/admin/AdminMerchants';
import AdminMerchantDetail from './pages/admin/AdminMerchantDetail';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminPlans from './pages/admin/AdminPlans';
import AdminInvoiceSettings from './pages/admin/AdminInvoiceSettings';
import AdminConnectApps from './pages/admin/AdminConnectApps';

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={getSafeNext(window.location.search) || '/dashboard'} />} />
      <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/dashboard" />} />
      <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/book/:merchantId" element={<BookingPage />} />
      <Route path="/connect" element={<ConnectPage />} />

      {/* Super Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute roles={['super_admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="merchants" element={<AdminMerchants />} />
        <Route path="merchants/:id" element={<AdminMerchantDetail />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="invoice-settings" element={<AdminInvoiceSettings />} />
        <Route path="connected-apps" element={<AdminConnectApps />} />
        <Route path="my-account" element={<MyAccountPage />} />
      </Route>

      {/* Merchant / Agent Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute roles={['merchant', 'agent']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<OverviewPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="channels" element={<ChannelsPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="ai-settings" element={<AISettingsPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="crm" element={<CRMPage />} />
        <Route path="extraction" element={<ExtractionTemplatesPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="my-account" element={<MyAccountPage />} />
      </Route>

      <Route path="/" element={
        user?.role === 'super_admin' ? <Navigate to="/admin" /> :
        user ? <Navigate to="/dashboard" /> :
        <Navigate to="/login" />
      } />
    </Routes>
  );
};

function ThemedToaster() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  return (
    <Toaster position="top-right" toastOptions={{
      style: {
        background: dark ? '#2A2A2A' : '#fff',
        color: dark ? '#FFFFFF' : '#111827',
        border: `1px solid ${dark ? '#3A3A3A' : '#E5E7EB'}`,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.15)',
      },
      success: { iconTheme: { primary: '#22C55E', secondary: dark ? '#2A2A2A' : '#fff' } },
      error: { iconTheme: { primary: '#EF4444', secondary: dark ? '#2A2A2A' : '#fff' } },
    }} />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <AppRoutes />
            <ThemedToaster />
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
